import { useCallback, useEffect, useState } from "react";
import {
  clearToken,
  executeIncidentAction,
  fetchIncidentDetail,
  fetchMe,
  fetchPlatformOperations,
  getToken,
  login as apiLogin,
  runPlatformDoctor,
  setToken,
  type AdminProfile,
} from "@/lib/api";
import { mapDoctorRun, mapIncidentActionResult, mapIncidentDetail, mapOperations, mapPulse, type DoctorRunResult } from "@/lib/operationsMappers";
import type {
  ActivityEvent,
  AttentionItem,
  IncidentActionResult,
  IncidentDetail,
  OperationsAssistantBriefing,
  OperatorTask,
  PlatformPulse,
  PlatformService,
  ServiceDetail,
  TenantHealthRow,
} from "@/types";

export function useOpsAuth() {
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    if (!getToken()) {
      setProfile(null);
      setAuthLoading(false);
      return;
    }
    try {
      const me = await fetchMe();
      setProfile(me);
    } catch {
      clearToken();
      setProfile(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const login = useCallback(
    async (email: string, password: string) => {
      const token = await apiLogin(email, password);
      setToken(token.access_token);
      await loadProfile();
    },
    [loadProfile],
  );

  const logout = useCallback(() => {
    clearToken();
    setProfile(null);
  }, []);

  return {
    profile,
    authLoading,
    isAuthenticated: profile != null,
    login,
    logout,
    reloadProfile: loadProfile,
  };
}

export type OpsAuthState = ReturnType<typeof useOpsAuth>;

export type PlatformOperationsState = {
  pulse: PlatformPulse | null;
  assistant: OperationsAssistantBriefing | null;
  attention: AttentionItem[];
  tenants: TenantHealthRow[];
  operatorTasks: OperatorTask[];
  services: PlatformService[];
  serviceDetails: Record<string, ServiceDetail>;
  activity: ActivityEvent[];
  platformVerifications: Array<Record<string, unknown>>;
  loading: boolean;
  error: string | null;
  needsAuth: boolean;
  refresh: () => Promise<void>;
  runDoctor: () => Promise<DoctorRunResult>;
  loadIncident: (incidentId: string) => Promise<IncidentDetail>;
  executeAction: (incidentId: string, actionId: string) => Promise<IncidentActionResult>;
  refreshBackground: () => Promise<void>;
};

export function usePlatformOperations(enabled: boolean): PlatformOperationsState {
  const [pulse, setPulse] = useState<PlatformPulse | null>(null);
  const [assistant, setAssistant] = useState<OperationsAssistantBriefing | null>(null);
  const [attention, setAttention] = useState<AttentionItem[]>([]);
  const [tenants, setTenants] = useState<TenantHealthRow[]>([]);
  const [operatorTasks, setOperatorTasks] = useState<OperatorTask[]>([]);
  const [services, setServices] = useState<PlatformService[]>([]);
  const [serviceDetails, setServiceDetails] = useState<Record<string, ServiceDetail>>({});
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [platformVerifications, setPlatformVerifications] = useState<
    Array<Record<string, unknown>>
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);

  const applyOperations = useCallback((data: ReturnType<typeof mapOperations>) => {
    setPulse(data.pulse);
    setAssistant(data.assistant);
    setAttention(data.attention);
    setTenants(data.tenants);
    setOperatorTasks(data.operatorTasks);
    setServices(data.services);
    setServiceDetails(data.serviceDetails);
    setActivity(data.activity);
    setPlatformVerifications(data.platformVerifications ?? []);
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    setNeedsAuth(false);
    try {
      const data = mapOperations(await fetchPlatformOperations());
      applyOperations(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load platform operations";
      if (message === "AUTH_REQUIRED") {
        setNeedsAuth(true);
        setPulse(null);
        setAssistant(null);
        setAttention([]);
        setTenants([]);
        setOperatorTasks([]);
        setServices([]);
        setServiceDetails({});
        setActivity([]);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [applyOperations, enabled]);

  const runDoctor = useCallback(async () => {
    const result = mapDoctorRun(await runPlatformDoctor());
    await refresh();
    return result;
  }, [refresh]);

  const loadIncident = useCallback(async (incidentId: string) => {
    return mapIncidentDetail(await fetchIncidentDetail(incidentId));
  }, []);

  const refreshBackground = useCallback(async () => {
    if (!enabled) return;
    try {
      const data = mapOperations(await fetchPlatformOperations());
      applyOperations(data);
    } catch {
      /* background refresh — non-blocking */
    }
  }, [applyOperations, enabled]);

  const executeAction = useCallback(
    async (incidentId: string, actionId: string) => {
      const result = mapIncidentActionResult(await executeIncidentAction(incidentId, actionId));
      // Refresh fleet Verification / Policy / Runtime Health / Attention so
      // Critical → Healthy updates without a manual page reload.
      await refreshBackground();
      return result;
    },
    [refreshBackground],
  );

  useEffect(() => {
    if (enabled) void refresh();
  }, [enabled, refresh]);

  return {
    pulse,
    assistant,
    attention,
    tenants,
    operatorTasks,
    services,
    serviceDetails,
    activity,
    platformVerifications,
    loading,
    error,
    needsAuth,
    refresh,
    runDoctor,
    loadIncident,
    executeAction,
    refreshBackground,
  };
}

/** @deprecated use usePlatformOperations */
export function usePlatformPulse(enabled: boolean) {
  const ops = usePlatformOperations(enabled);
  return {
    pulse: ops.pulse,
    loading: ops.loading,
    error: ops.error,
    needsAuth: ops.needsAuth,
    refresh: ops.refresh,
  };
}

export { mapPulse };
